import { Injectable, inject } from '@angular/core';
import { HttpClient, HttpParams } from '@angular/common/http';
import { Observable } from 'rxjs';
import { environment } from '../../../../environments/environment';
import {
  AvailableVariableNamespace,
  Template,
  TemplateAssetResponse,
  TemplatePreviewRequest,
  TemplatePreviewResponse,
  TemplateRequest,
  TemplateSearchParams,
  TemplateStatus,
  TemplateVariable,
  TemplateVariableRequest,
} from '../models/template.model';

@Injectable({ providedIn: 'root' })
export class TemplateApiService {
  private readonly http = inject(HttpClient);
  private readonly base = `${environment.apiUrl}api/templates`;

  // GET /api/templates?status=&categoryId=
  searchTemplates(params: TemplateSearchParams = {}): Observable<Template[]> {
    let httpParams = new HttpParams();
    if (params.status) httpParams = httpParams.set('status', params.status);
    if (params.categoryId) httpParams = httpParams.set('categoryId', params.categoryId);
    return this.http.get<Template[]>(this.base, { params: httpParams });
  }

  // GET /api/templates/{id}
  getTemplateById(id: string): Observable<Template> {
    return this.http.get<Template>(`${this.base}/${id}`);
  }

  // POST /api/templates
  createTemplate(req: TemplateRequest): Observable<Template> {
    return this.http.post<Template>(this.base, req);
  }

  // PUT /api/templates/{id}
  updateTemplate(id: string, req: TemplateRequest): Observable<Template> {
    return this.http.put<Template>(`${this.base}/${id}`, req);
  }

  // DELETE /api/templates/{id}
  deleteTemplate(id: string): Observable<void> {
    return this.http.delete<void>(`${this.base}/${id}`);
  }

  // PATCH /api/templates/{id}/status?value=...
  changeStatus(id: string, value: TemplateStatus): Observable<Template> {
    const params = new HttpParams().set('value', value);
    return this.http.patch<Template>(`${this.base}/${id}/status`, null, { params });
  }

  // POST /api/templates/{id}/duplicate
  duplicateTemplate(id: string): Observable<Template> {
    return this.http.post<Template>(`${this.base}/${id}/duplicate`, {});
  }

  // GET /api/templates/{id}/variables
  listVariables(id: string): Observable<TemplateVariable[]> {
    return this.http.get<TemplateVariable[]>(`${this.base}/${id}/variables`);
  }

  // POST /api/templates/{id}/variables
  addVariable(id: string, req: TemplateVariableRequest): Observable<TemplateVariable> {
    return this.http.post<TemplateVariable>(`${this.base}/${id}/variables`, req);
  }

  // DELETE /api/templates/{id}/variables/{variableId}
  removeVariable(id: string, variableId: string): Observable<void> {
    return this.http.delete<void>(`${this.base}/${id}/variables/${variableId}`);
  }

  // POST /api/templates/{id}/preview
  preview(id: string, req: TemplatePreviewRequest = {}): Observable<TemplatePreviewResponse> {
    return this.http.post<TemplatePreviewResponse>(`${this.base}/${id}/preview`, req);
  }

  // GET /api/templates/available-variables
  getAvailableVariables(): Observable<AvailableVariableNamespace[]> {
    return this.http.get<AvailableVariableNamespace[]>(`${this.base}/available-variables`);
  }

  // POST /api/templates/assets (multipart) — solo imágenes
  uploadAsset(file: File): Observable<TemplateAssetResponse> {
    const fd = new FormData();
    fd.append('file', file);
    return this.http.post<TemplateAssetResponse>(`${this.base}/assets`, fd);
  }

  // POST /api/templates/assets/documents (multipart) — PDF, DOC, DOCX, XLS, XLSX, PPT, PPTX, TXT
  uploadDocument(file: File): Observable<TemplateAssetResponse> {
    const fd = new FormData();
    fd.append('file', file);
    return this.http.post<TemplateAssetResponse>(`${this.base}/assets/documents`, fd);
  }
}
